// AGC: Quantity/value field — number input with voice number extraction
import React from 'react'
import { FieldMicButton } from './FieldMicButton'
import { clampDecimalStr } from '../../../utils'
import type { VoiceFieldProps } from './types'

// AGC: Portuguese written number lookup (reused from existing voice input logic)
const WRITTEN_NUMBERS: Record<string, string> = {
  'zero': '0', 'um': '1', 'uma': '1', 'dois': '2', 'duas': '2',
  'três': '3', 'tres': '3', 'quatro': '4', 'cinco': '5',
  'seis': '6', 'sete': '7', 'oito': '8', 'nove': '9', 'dez': '10',
  'vinte': '20', 'trinta': '30', 'quarenta': '40', 'cinquenta': '50',
  'cem': '100', 'duzentos': '200', 'trezentos': '300', 'mil': '1000',
}

/** AGC: Extract a number from Portuguese speech transcript */
function extractNumber(transcript: string): string | null {
  // AGC: Try digit extraction first
  const digits = transcript.match(/\d+([.,]\d+)?/g)
  if (digits && digits.length > 0) return digits.join('').replace(',', '.')
  // AGC: Try written numbers
  const words = transcript.toLowerCase().split(/\s+/)
  for (const word of words) {
    if (WRITTEN_NUMBERS[word]) return WRITTEN_NUMBERS[word]
  }
  return null
}

export const QuantityField: React.FC<VoiceFieldProps> = ({
  fieldName,
  info,
  inputValues,
  setInputValues,
  wizardMode,
  onPermissionBlocked,
}) => {
  // AGC: Sizing classes — bigger in wizard mode to fill the full-screen card layout
  const sz = wizardMode
    ? { card: 'p-5', label: 'text-base', input: 'px-4 py-3 text-lg', mb: 'mb-3' }
    : { card: 'p-2', label: 'text-xs', input: 'px-2 py-1.5 text-sm', mb: 'mb-1.5' }

  return (
    <div className={`${sz.card} bg-red-500/10 border border-red-500/30 rounded-xl`}>
      <div className={`flex items-center justify-between ${sz.mb}`}>
        <span className={`text-red-300 ${sz.label} font-medium`}>{info.fieldLabel}</span>
      </div>
      {info.reason && (
        <p className="text-white/40 text-xs mt-0.5 mb-1.5">{info.reason}</p>
      )}
      <div className="flex items-center gap-2">
        <input
          type="number"
          placeholder="0"
          value={inputValues[fieldName] || ''}
          onChange={(e) => setInputValues(prev => ({ ...prev, [fieldName]: clampDecimalStr(e.target.value) }))}
          className={`flex-1 ${sz.input} bg-white/10 border border-white/20 rounded text-white placeholder-white/30 focus:outline-none focus:border-emerald-500`}
        />
        {info.allowVoiceInput !== false && (
          <FieldMicButton
            fieldName={fieldName}
            variant="default"
            onResult={(transcript) => {
              const num = extractNumber(transcript)
              if (num) {
                setInputValues(prev => ({ ...prev, [fieldName]: num }))
              } else {
                setInputValues(prev => ({ ...prev, [fieldName]: transcript }))
              }
            }}
            onPermissionBlocked={onPermissionBlocked}
          />
        )}
      </div>
    </div>
  )
}
